/**
 * Native thermal printing bridge (Android app only).
 *
 * Talks to the custom `SmartoPrinter` Capacitor plugin (DantSu ESC/POS) so
 * the POS can print to Bluetooth / USB thermal printers inside the native
 * SmartoERP Android app. Works even though the app loads smartoerp.com
 * remotely, because Capacitor injects window.Capacitor.Plugins regardless of
 * server.url. Network printers keep using ePOS; iOS/AirPrint and browser
 * printing are untouched — every entry point is guarded by
 * `isNativeThermalAvailable()`, which is false on web, iPad and desktop.
 */
import { isCapacitorNative, isAndroid } from './platformDetect';
import { tailorReceiptLines } from './tailorReceipt';

function plugin() {
  try { return window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.SmartoPrinter; }
  catch { return null; }
}

export function isNativeThermalAvailable() {
  try { return isCapacitorNative() && isAndroid() && !!plugin(); } catch { return false; }
}

/** Build a plain-text receipt for Sunmi's built-in printer (no markup). */
function buildPlainReceipt(payload = {}, chars = 32) {
  const cur = payload.currency || '';
  const num = (v) => Number(v || 0).toFixed(2);
  const center = (s) => { const pad = Math.max(0, Math.floor((chars - s.length) / 2)); return ' '.repeat(pad) + s; };
  const lr = (l, r) => { const space = Math.max(1, chars - l.length - r.length); return l + ' '.repeat(space) + r; };
  const out = [];
  if (payload.company) out.push(center(payload.company));
  if (payload.title) out.push(center(payload.title));
  if (payload.order_number) out.push(`Order: ${payload.order_number}`);
  if (payload.date) out.push(payload.date);
  out.push('-'.repeat(chars));
  (payload.items || []).forEach((it) => out.push(lr(`${it.qty || 1}x ${it.name || ''}`, `${cur}${num(it.subtotal)}`)));
  out.push('-'.repeat(chars));
  if (payload.subtotal != null) out.push(lr('Subtotal (excl. VAT)', `${cur}${num(payload.subtotal)}`));
  if (payload.tax != null) out.push(lr('VAT Amount', `${cur}${num(payload.tax)}`));
  if (payload.total != null) out.push(lr('Total (incl. VAT)', `${cur}${num(payload.total)}`));
  if (payload.footer) {
    out.push('');
    String(payload.footer).split('\n').forEach((ln) => {
      const t = ln.trim();
      if (t) out.push(center(t));
    });
  }
  return out.join('\n');
}

/** List paired/available printers for a transport: 'bluetooth' | 'usb'. */
export async function listNativePrinters(type = 'bluetooth') {
  const p = plugin();
  if (!p) return [];
  try {
    const res = await p.listPrinters({ type });
    return (res && res.printers) ? res.printers : [];
  } catch { return []; }
}

/** Ask for runtime Bluetooth/USB permission (Android 12+ needs this). */
export async function requestNativePermissions(type = 'bluetooth') {
  const p = plugin();
  if (!p) return false;
  try { const r = await p.requestPermissions({ type }); return !!(r && r.granted); } catch { return false; }
}

/**
 * iter898 — Explicit USB-only permission request. Triggers the OS
 * grant dialog for `deviceName` (or all detected USB printers if the
 * name is omitted). Meant to be called from a "Grant USB Permission"
 * button that stays visible after the user rejects once.
 */
export async function requestUsbPermission(deviceName = '') {
  const p = plugin();
  if (!p || !p.requestUsbPermission) return { ok: false, error: 'Native plugin missing — rebuild the APK.', requested: 0 };
  try {
    const r = await p.requestUsbPermission({ deviceName });
    return { ok: true, requested: (r && r.requested) || 0 };
  } catch (e) {
    return { ok: false, error: (e && (e.message || e)) || 'USB permission request failed', requested: 0 };
  }
}

/**
 * iter898 — Grant + diagnostic-print flow in one tap. Requests USB
 * permission if missing, then fires a small "Smarto USB test" ticket
 * to the first available USB printer so the operator can confirm the
 * whole pipeline (permission → connection → paper) works.
 */
export async function usbDiagnosticPrint() {
  const p = plugin();
  if (!p) return { ok: false, error: 'Native thermal printing is only available in the SmartoERP Android app.' };
  try {
    // 1. Kick the OS dialog for every plugged-in USB printer.
    if (p.requestUsbPermission) {
      try { await p.requestUsbPermission({}); } catch { /* best effort */ }
    }
    // 2. Enumerate + pick the first USB printer.
    const list = await listNativePrinters('usb');
    if (!list || list.length === 0) {
      return { ok: false, error: 'No USB printer detected. Plug in the printer and try again.' };
    }
    const target = list[0];
    // 3. Build a short diagnostic ticket.
    const stamp = new Date().toLocaleString();
    const text = [
      '     SMARTO USB TEST     ',
      '--------------------------------',
      `Device : ${target.deviceName || target.id || 'USB printer'}`,
      target.vendorId ? `Vendor : 0x${Number(target.vendorId).toString(16).padStart(4, '0').toUpperCase()}` : '',
      target.productId ? `Product: 0x${Number(target.productId).toString(16).padStart(4, '0').toUpperCase()}` : '',
      `Time   : ${stamp}`,
      '--------------------------------',
      '   If you can read this, ',
      '  USB permission + connection',
      '     are both working.',
      '',
      '     Smarto ERP',
      '',
    ].filter(Boolean).join('\n');
    // 4. Fire the print — the native plugin now blocks on permission.
    await p.printText({
      type: 'usb',
      id: String(target.id || ''),
      printerWidthMM: 48,
      printerNbrCharactersPerLine: 32,
      printerDpi: 203,
      text,
    });
    return { ok: true, device: target.deviceName || String(target.id) };
  } catch (e) {
    const msg = (e && (e.message || e)) || 'USB diagnostic print failed';
    return { ok: false, error: String(msg) };
  }
}

/** Scan the local Wi-Fi subnet for ESC/POS network printers (port 9100). */
export async function discoverNetworkPrinters(port = 9100) {
  const p = plugin();
  if (!p) return { available: false, printers: [] };
  try {
    const res = await p.discoverNetwork({ port });
    return { available: true, printers: (res && res.printers) || [], subnet: res && res.subnet };
  } catch (e) {
    return { available: true, printers: [], error: (e && (e.message || e)) || 'scan failed' };
  }
}

/**
 * Print pre-formatted DantSu text to a NETWORK printer over raw TCP
 * (default port 9100) via the native plugin. This bypasses the browser's
 * self-signed-HTTPS / mixed-content / CORS limitations that make
 * ePOS-over-HTTPS unreliable inside the Android WebView — the #1 reason
 * "nothing prints" on Android network printers. Android-app only.
 */
export async function printNativeTcp({ address, port = 9100, text, widthMM = 72, chars = 48, dpi = 203, openDrawer = false }) {
  const p = plugin();
  if (!p) return { ok: false, error: 'Native thermal printing is only available in the SmartoERP Android app.' };
  if (!address) return { ok: false, error: 'Network printer has no IP address.' };
  try {
    await p.printText({
      type: 'tcp',
      address,
      port: Number(port) || 9100,
      printerWidthMM: widthMM,
      printerNbrCharactersPerLine: chars,
      printerDpi: dpi,
      text,
      openDrawer,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e && (e.message || e)) || 'Native TCP print failed' };
  }
}

/** Drawer kick without printing (APK 1.0.50+: network printers + Sunmi; older APKs return ok:false). */
export async function kickNativeDrawer({ type, address, port = 9100 }) {
  const p = plugin();
  const sunmi = type === 'sunmi';
  if (!p || (!sunmi && !address)) return { ok: false };
  try { await p.kickDrawer(sunmi ? { type: 'sunmi' } : { address, port: Number(port) || 9100 }); return { ok: true }; }
  catch (e) { return { ok: false, error: (e && (e.message || e)) || 'kick failed' }; }
}

const PAPER = { '58mm': { widthMM: 48, chars: 32 }, '80mm': { widthMM: 72, chars: 48 } };

/* iter930 — Render a logo URL into a DantSu-compatible <img> hex string.
 * The hex is the raw ESC/POS "GS v 0" raster command (1D 76 30 00, xL xH,
 * yL yH, then 1-bit rows) which DantSu's PrinterTextParserImg accepts.
 * The URL is already the thermally-optimised monochrome PNG (branch →
 * thermal → main logo fallback resolved upstream in posPrint.js). */
export async function _logoHexFromUrl(logoUrl, paperMm) {
  if (!logoUrl || typeof document === 'undefined') return null;
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    const t = setTimeout(() => reject(new Error('logo load timeout')), 4000);
    im.onload = () => { clearTimeout(t); resolve(im); };
    im.onerror = () => { clearTimeout(t); reject(new Error('logo load failed')); };
    im.src = logoUrl;
  });
  const maxW = paperMm >= 70 ? 384 : 256; // printable dots
  const w = Math.min(img.width || maxW, maxW);
  const h = Math.max(1, Math.round((img.height || 1) * (w / (img.width || 1))));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const dat = ctx.getImageData(0, 0, w, h).data;
  const bytesPerLine = Math.ceil(w / 8);
  const hx = (n) => (n & 0xff).toString(16).padStart(2, '0');
  let hex = '1d7630' + '00' + hx(bytesPerLine) + hx(bytesPerLine >> 8) + hx(h) + hx(h >> 8);
  for (let y = 0; y < h; y++) {
    for (let bx = 0; bx < bytesPerLine; bx++) {
      let b = 0;
      for (let bit = 0; bit < 8; bit++) {
        const x = bx * 8 + bit;
        if (x < w) {
          const i = (y * w + x) * 4;
          const lum = 0.299 * dat[i] + 0.587 * dat[i + 1] + 0.114 * dat[i + 2];
          if (dat[i + 3] > 127 && lum < 160) b |= (0x80 >> bit);
        }
      }
      hex += hx(b);
    }
  }
  return hex.toUpperCase();
}

/** Convert a Smarto receipt payload to DantSu formatted-text syntax. */
function buildFormattedReceipt(payload = {}, chars = 48, extras = {}) {
  const cur = payload.currency || '';
  // iter910 — Reserve a 3-char safe margin on each side to keep the
  // currency label / totals from being clipped by the printer's
  // physical dead zone. Every ruler + right-aligned line uses the
  // reduced inner width.
  const innerChars = Math.max(chars - 6, 20);
  const line = '-'.repeat(innerChars);
  const num = (v) => Number(v || 0).toFixed(2);
  const rows = [];
  // iter930 — Logo at the top: DantSu renders <img> from a GS v 0 raster
  // hex string prepared by _logoHexFromUrl (branch → thermal → main logo
  // fallback already resolved into payload.logo_url by posPrint.js).
  if (extras.logoHex) rows.push(`[C]<img>${extras.logoHex}</img>`);
  // iter900 — 58mm rolls (32 chars/line) overflow if we use <font size='big'>
  // for the company name — long tenant names get chopped or wrapped weirdly.
  // Fall back to a normal-size bold header on narrow paper; keep the big
  // header only for 80mm receipts where there's room to breathe.
  const isNarrow = chars <= 32;
  if (payload.company) {
    rows.push(isNarrow
      ? `[C]<b>${payload.company}</b>`
      : `[C]<b><font size='big'>${payload.company}</font></b>`);
  }
  if (payload.title) rows.push(`[C]${payload.title}`);
  if (payload.order_number) rows.push(`[L]Order: ${payload.order_number}`);
  if (payload.date) rows.push(`[L]${payload.date}`);
  rows.push(`[C]${line}`);
  (payload.items || []).forEach((it) => {
    rows.push(`[L]${it.qty || 1}x ${it.name || ''}[R]${cur}${num(it.subtotal)}`);
  });
  rows.push(`[C]${line}`);
  if (payload.subtotal != null) rows.push(`[L]Subtotal (excl. VAT)[R]${cur}${num(payload.subtotal)}`);
  if (payload.tax != null) rows.push(`[L]VAT Amount[R]${cur}${num(payload.tax)}`);
  if (payload.total != null) rows.push(`[L]<b>Total (incl. VAT)</b>[R]<b>${cur}${num(payload.total)}</b>`);
  rows.push('[C] ');
  // iter917 — Tailor MEASUREMENTS / MODIFICATION section (bilingual EN/AR).
  const tailor = tailorReceiptLines(payload.metadata);
  if (tailor) {
    rows.push(`[C]${line}`);
    rows.push(`[C]<b>${tailor.title}</b>`);
    tailor.rows.forEach((r) => rows.push(`[L]${r.label}[R]${r.value}`));
    rows.push(`[C]${line}`);
  }
  // iter930 — ZATCA TLV QR on the native (USB/Bluetooth) receipt, matching
  // what the ePOS network path already prints. DantSu renders <qrcode>.
  if (extras.qrData) {
    rows.push(`[C]<qrcode size='25'>${extras.qrData}</qrcode>`);
    rows.push('[C]ZATCA E-Invoice QR');
  }
  // iter900 — footer often contains multi-line text (VAT #, address, phone,
  // "Thank you" etc). Splitting on \n and emitting each line as its own
  // centered row keeps every line properly aligned instead of jamming
  // them all together as one long overflowing string.
  if (payload.footer) {
    String(payload.footer).split('\n').forEach((ln) => {
      const t = ln.trim();
      if (t) rows.push(`[C]${t}`);
    });
  }
  rows.push('[C] ');
  return rows.join('\n');
}

/**
 * Print a receipt payload to a Bluetooth/USB printer via the native plugin.
 * @param {object} printer  Smarto printer config (connection_type, device_address, paper_width)
 * @param {object} payload  receipt payload (same shape the /test endpoint returns)
 */
export async function printNativeThermal(printer, payload, { openDrawer = false } = {}) {
  const p = plugin();
  if (!p) return { ok: false, error: 'Native thermal printing is only available in the SmartoERP Android app.' };
  const ct = printer.connection_type;
  const paper = PAPER[printer.paper_width] || PAPER['80mm'];
  // Sunmi built-in printer — plain text, no pairing/address needed.
  if (ct === 'sunmi') {
    try {
      await p.printText({ type: 'sunmi', text: buildPlainReceipt(payload, paper.chars), openDrawer });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: (e && (e.message || e)) || 'Sunmi print failed' };
    }
  }

  const type = (ct === 'usb') ? 'usb' : 'bluetooth';
  await requestNativePermissions(type);
  // iter930 — Prepare logo raster + ZATCA QR for the DantSu parser. Both are
  // best-effort: any failure silently degrades to a text-only receipt.
  // Support finding — pre-convert the logo BEFORE the native call and LOG any
  // conversion failure instead of silently swallowing it (was hiding broken
  // logo URLs on the Test Print / Kitchen / Bar flows). Print still proceeds
  // without the logo rather than failing the whole receipt.
  let logoHex = null;
  let qrData = null;
  try {
    if (payload.logo_url) {
      // iter934 — convert the (possibly cross-origin) server logo URL to a
      // data URI FIRST so _logoHexFromUrl's canvas read never runs on a remote
      // URL (which taints the canvas and throws on getImageData in the APK).
      let logoSrc = payload.logo_url;
      try {
        const { logoUrlToDataUri } = await import('./thermalLogoOptimizer');
        const converted = await logoUrlToDataUri(logoSrc, paper.widthMM);
        if (converted) logoSrc = converted;
      } catch (_) { /* fall back to raw url */ }
      logoHex = await _logoHexFromUrl(logoSrc, paper.widthMM);
    }
  } catch (e) {
    logoHex = null;
    console.error('[thermal] logo conversion failed (native path); printing without logo:', payload?.logo_url, e);
  }
  try {
    if (payload.show_zatca_qr !== false && payload.total != null) {
      const { buildZatcaQrPayload } = await import('./eposPrint');
      qrData = buildZatcaQrPayload(payload) || null;
    }
  } catch { qrData = null; }
  try {
    // iter911 — 3-char safe margin on each side for the DantSu driver.
    // Pulling the printer's declared char budget in by 6 stops the
    // right-aligned "SAR" / totals from being clipped by the physical
    // dead-zone at the paper edge. Floor of 20 so a wildly small
    // custom value never collapses the layout.
    const safeChars = Math.max(paper.chars - 6, 20);
    await p.printText({
      type,
      id: printer.device_address || '',
      printerWidthMM: paper.widthMM,
      printerNbrCharactersPerLine: safeChars,
      printerDpi: 203,
      text: buildFormattedReceipt(payload, safeChars, { logoHex, qrData }),
      openDrawer,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e && (e.message || e)) || 'Native print failed' };
  }
}
